import {
  afterNextRender,
  Component,
  DOCUMENT,
  ElementRef,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';

const PAGE_URL = 'https://www.resiliencesoft.com/ourproducts/resiliohub';
const PAGE_TITLE = 'ResilioHub: WhatsApp CRM & AI Automation Platform | ResilienceSoft';
const PAGE_DESCRIPTION =
  'Discover ResilioHub by ResilienceSoft, a WhatsApp Business API CRM with AI chatbots, shared team inbox, broadcast campaigns, lead management and workflow automation.';
const PAGE_KEYWORDS =
  'WhatsApp CRM software, WhatsApp Business API platform, WhatsApp CRM India, AI-powered WhatsApp automation, WhatsApp chatbot software, business messaging platform, ResilioHub, ResilienceSoft';

@Component({
  selector: 'app-resiliohub',
  templateUrl: './resiliohub.component.html',
  styleUrls: ['./resiliohub.component.css'],
  standalone: false,
})
export class ResiliohubComponent implements OnInit, OnDestroy {
  readonly productUrl = 'https://resiliohub.com/';
  activeAudience = 0;
  faqQuery = '';
  openFaq: string | null = null;
  private pending: HTMLElement[] = [];
  private revealCleanup?: () => void;
  private addedNodes: HTMLElement[] = [];

  heroChips = [
    { icon: 'fas fa-robot', label: 'AI Chatbots' },
    { icon: 'fas fa-inbox', label: 'Shared Inbox' },
    { icon: 'fas fa-bullhorn', label: 'Broadcasts' },
    { icon: 'fas fa-project-diagram', label: 'Automation' },
  ];

  hubNodes = [
    { icon: 'fas fa-comments', label: 'Conversations' },
    { icon: 'fas fa-address-book', label: 'Contacts' },
    { icon: 'fas fa-funnel-dollar', label: 'Sales' },
    { icon: 'fas fa-cogs', label: 'Automation' },
    { icon: 'fas fa-headset', label: 'Support' },
    { icon: 'fas fa-chart-line', label: 'Campaigns' },
  ];

  features = [
    {
      icon: 'fab fa-whatsapp',
      title: 'WhatsApp Business API Integration',
      text: 'Connect business messaging workflows through the WhatsApp Business Platform and manage customer communications using a centralized dashboard. ResilioHub supports business messaging use cases, including marketing, utility and authentication messages, subject to applicable platform requirements and messaging policies.',
      wide: true,
    },
    {
      icon: 'fas fa-users-cog',
      title: 'WhatsApp CRM and Lead Management',
      text: 'Organize customer contacts, maintain conversation history, categorize leads and track opportunities through visual sales pipelines. Help your team manage enquiries systematically and follow up with potential customers.',
    },
    {
      icon: 'fas fa-bullhorn',
      title: 'WhatsApp Broadcast Messaging',
      text: 'Create and manage WhatsApp broadcast campaigns for customer engagement, promotional communication and business updates. Use approved message templates where required and monitor available delivery and engagement metrics.',
    },
    {
      icon: 'fas fa-robot',
      title: 'AI Chatbots and Custom AI Agents',
      text: 'Automate common customer enquiries, answer frequently asked questions, collect lead information and guide customers through predefined conversation flows. AI-powered workflows can help reduce repetitive manual tasks while allowing human agents to handle conversations that need personal attention.',
      wide: true,
    },
    {
      icon: 'fas fa-project-diagram',
      title: 'Workflow Automation',
      text: 'Build automated workflows for welcome messages, enquiry handling, lead qualification, customer follow-ups and other business processes. Use triggers, conditions and integrations to connect customer communication with operational activities.',
    },
    {
      icon: 'fas fa-inbox',
      title: 'Shared Team Inbox',
      text: 'Allow sales, support and customer service teams to manage conversations through a shared workspace. Assign chats, maintain customer context and coordinate responses to improve collaboration and reduce missed enquiries.',
    },
    {
      icon: 'fas fa-layer-group',
      title: 'Omnichannel Customer Communication',
      text: 'Manage supported messaging channels, including WhatsApp, Instagram Direct and Telegram, through a unified workspace. Available channels and features depend on the relevant integrations and account configuration.',
    },
    {
      icon: 'fas fa-plug',
      title: 'CRM Integrations, APIs and Webhooks',
      text: 'Connect ResilioHub with supported business applications, websites, CRM systems and custom software through available APIs, webhooks and integrations. Build workflows that synchronize customer information and messaging events with your existing technology stack.',
    },
    {
      icon: 'fas fa-chart-pie',
      title: 'Analytics and Campaign Reporting',
      text: 'Review available campaign metrics, delivery information, conversation activity and team performance to understand customer engagement and identify opportunities to improve communication workflows.',
    },
  ];

  benefits = [
    {
      icon: 'fas fa-bolt',
      title: 'Improve Customer Response Times',
      text: 'Automate routine responses and help your team manage customer enquiries more efficiently.',
    },
    {
      icon: 'fas fa-stream',
      title: 'Organize Leads in One Place',
      text: 'Maintain structured customer records and track leads through defined sales stages instead of relying on scattered messages and manual follow-ups.',
    },
    {
      icon: 'fas fa-magic',
      title: 'Automate Repetitive Business Tasks',
      text: 'Reduce manual work associated with welcome messages, frequently asked questions, lead qualification and customer communication workflows.',
    },
    {
      icon: 'fas fa-hands-helping',
      title: 'Improve Team Collaboration',
      text: 'Give sales and support teams a shared view of customer conversations so they can coordinate responses and maintain context.',
    },
    {
      icon: 'fas fa-chart-bar',
      title: 'Make Campaign Performance Measurable',
      text: 'Use available messaging and campaign reports to evaluate communication activity and make more informed marketing decisions.',
    },
    {
      icon: 'fas fa-network-wired',
      title: 'Connect Messaging with Existing Systems',
      text: 'Integrate business communication with supported CRM tools, applications, APIs and operational workflows to reduce disconnected processes.',
    },
  ];

  audiences = [
    {
      icon: 'fas fa-store',
      title: 'Small and Medium-Sized Businesses',
      text: 'Manage customer enquiries, automate routine communication and organize leads without relying entirely on manual processes.',
      tags: ['Enquiries', 'Auto-replies', 'Lead lists'],
    },
    {
      icon: 'fas fa-shopping-cart',
      title: 'E-commerce Businesses',
      text: 'Support customer enquiries, order-related communication and customer engagement workflows through available integrations and messaging features.',
      tags: ['Order updates', 'Integrations', 'Engagement'],
    },
    {
      icon: 'fas fa-graduation-cap',
      title: 'Educational Institutions',
      text: 'Manage prospective student enquiries, admission-related communication, reminders and follow-ups using configured business workflows.',
      tags: ['Admissions', 'Reminders', 'Follow-ups'],
    },
    {
      icon: 'fas fa-heartbeat',
      title: 'Healthcare and Appointment-Based Businesses',
      text: 'Organize customer enquiries and appointment-related communication. Configure messaging according to applicable privacy, consent and industry requirements.',
      tags: ['Appointments', 'Consent-aware', 'Enquiries'],
    },
    {
      icon: 'fas fa-bullseye',
      title: 'Marketing Agencies',
      text: 'Manage client messaging workflows, campaign communication and supported account integrations from a centralized platform.',
      tags: ['Client accounts', 'Campaigns', 'Central hub'],
    },
    {
      icon: 'fas fa-user-tie',
      title: 'Sales and Customer Support Teams',
      text: 'Assign customer conversations, maintain lead information, automate routine queries and coordinate team responses.',
      tags: ['Chat assignment', 'Lead info', 'Team replies'],
    },
  ];

  advantages = [
    'A centralized platform for supported messaging and customer management workflows.',
    'CRM-based lead organization and conversation management.',
    'Automation tools for routine enquiries and business processes.',
    'AI chatbot and custom AI agent capabilities.',
    'Integration options for connecting messaging with business applications.',
    'A product developed by ResilienceSoft, a technology solutions company.',
  ];

  traditional = [
    'Separate apps for messaging and lead tracking',
    'Team coordination over scattered chats',
    'Manual follow-ups and repeated answers',
    'Reporting stitched together by hand',
  ];

  connected = [
    'Supported channels in one unified workspace',
    'CRM pipelines linked to every conversation',
    'Chatbots and workflows handle routine enquiries',
    'Campaign and team reports in one dashboard',
  ];

  faqs = [
    {
      q: 'What is ResilioHub?',
      a: 'ResilioHub is a WhatsApp Business API and customer engagement platform developed by ResilienceSoft. It combines business messaging, CRM, shared inbox, broadcast campaigns, chatbot automation and integrations.',
    },
    {
      q: 'Is ResilioHub a WhatsApp CRM software?',
      a: 'Yes. ResilioHub provides CRM-related capabilities for organizing contacts, managing customer conversations, categorizing leads and tracking sales opportunities alongside supported business messaging features.',
    },
    {
      q: 'Does ResilioHub support WhatsApp Business API?',
      a: 'ResilioHub supports WhatsApp Business API-based messaging workflows. Access to particular features depends on account setup, available integrations and the applicable Meta policies.',
    },
    {
      q: 'Can ResilioHub automate WhatsApp messages?',
      a: 'Yes. ResilioHub provides automation features for configured replies, chatbot flows, customer enquiries and business workflows. Outbound messaging must follow applicable consent, template and platform requirements.',
    },
    {
      q: 'Does ResilioHub support AI chatbots?',
      a: 'Yes. ResilioHub offers AI chatbot and custom AI agent capabilities for automating suitable customer conversations and business workflows.',
    },
    {
      q: 'Can multiple team members manage WhatsApp conversations?',
      a: 'ResilioHub supports a shared team inbox and multi-agent collaboration features, subject to the selected configuration and plan.',
    },
    {
      q: 'Can ResilioHub integrate with existing business software?',
      a: 'ResilioHub offers APIs, webhooks and supported integrations to connect messaging workflows with business applications. Specific integration requirements should be confirmed before implementation.',
    },
    {
      q: 'Who develops ResilioHub?',
      a: 'ResilioHub is developed by ResilienceSoft, a technology solutions company offering software development and digital business services.',
    },
    {
      q: 'Where can I learn more about ResilioHub?',
      a: 'Visit the official website at https://resiliohub.com/ to explore product features, available plans, integrations and trial or demo options.',
    },
  ];

  constructor(
    private title: Title,
    private meta: Meta,
    private host: ElementRef<HTMLElement>,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object,
    private zone: NgZone
  ) {
    // Runs only in the browser, after hydration has finished.
    afterNextRender(() => this.initReveal());
  }

  ngOnInit(): void {
    this.openFaq = this.faqs[0].q;
    this.title.setTitle(PAGE_TITLE);
    this.meta.removeTag('name=description');
    this.meta.removeTag('name=keywords');
    this.meta.addTags([
      { name: 'description', content: PAGE_DESCRIPTION },
      { name: 'keywords', content: PAGE_KEYWORDS },
    ]);
    this.meta.updateTag({ property: 'og:title', content: PAGE_TITLE });
    this.meta.updateTag({ property: 'og:description', content: PAGE_DESCRIPTION });
    this.meta.updateTag({ property: 'og:url', content: PAGE_URL });
    this.meta.updateTag({ property: 'og:type', content: 'product' });
    this.addHeadNodes();
  }

  // Hides only sections below the fold, then fades them in on scroll.
  // A timed fallback guarantees nothing can stay hidden.
  private initReveal(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const limit = () => window.innerHeight * 0.92;
    this.pending = Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>('.reveal')).filter(
      (el) => el.getBoundingClientRect().top > limit()
    );
    this.pending.forEach((el) => el.classList.add('rh-hidden'));

    let frame = 0;
    const check = () => {
      frame = 0;
      this.pending = this.pending.filter((el) => {
        if (el.getBoundingClientRect().top < limit()) {
          el.classList.remove('rh-hidden');
          return false;
        }
        return true;
      });
      if (!this.pending.length) {
        this.revealCleanup?.();
      }
    };
    const onScroll = () => {
      if (!frame) {
        frame = requestAnimationFrame(check);
      }
    };
    const fallback = setTimeout(() => {
      this.pending.forEach((el) => el.classList.remove('rh-hidden'));
      this.pending = [];
      this.revealCleanup?.();
    }, 3000);

    this.zone.runOutsideAngular(() => {
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
    });
    this.revealCleanup = () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(frame);
      clearTimeout(fallback);
      this.revealCleanup = undefined;
    };
  }

  ngOnDestroy(): void {
    this.revealCleanup?.();
    this.addedNodes.forEach((node) => node.remove());
    ['og:title', 'og:description', 'og:url', 'og:type'].forEach((p) =>
      this.meta.removeTag(`property="${p}"`)
    );
  }

  setAudience(index: number): void {
    this.activeAudience = index;
  }

  get filteredFaqs() {
    const query = this.faqQuery.trim().toLowerCase();
    return query
      ? this.faqs.filter((f) => (f.q + ' ' + f.a).toLowerCase().includes(query))
      : this.faqs;
  }

  toggleFaq(question: string): void {
    this.openFaq = this.openFaq === question ? null : question;
  }

  // Canonical link plus SoftwareApplication and FAQPage structured data.
  private addHeadNodes(): void {
    const head = this.document.head;
    head.querySelectorAll('link[rel="canonical"], script[data-rh-schema]').forEach((n) => n.remove());

    const canonical = this.document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    canonical.setAttribute('href', PAGE_URL);
    head.appendChild(canonical);
    this.addedNodes.push(canonical);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'ResilioHub',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        url: this.productUrl,
        description: PAGE_DESCRIPTION,
        publisher: {
          '@type': 'Organization',
          name: 'ResilienceSoft',
          url: 'https://www.resiliencesoft.com/',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: this.faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ];
    schemas.forEach((schema) => {
      const script = this.document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-rh-schema', '');
      script.textContent = JSON.stringify(schema);
      head.appendChild(script);
      this.addedNodes.push(script);
    });
  }
}
